import {
  createAuthenticatedAuthorshipProfileRepository,
  decodeAuthorshipProfileProjectionV1,
} from "./account/authorship-profile-repository.js";
import {
  browserAccountStateSessionRepository,
} from "./account/account-state-session-repository.js";
import {
  historicalAuthorshipProfileProjection,
} from "./authorship-profile-values.js";
import { initializeBrowserLocalAuthor } from "./browser-user-state.js";
import {
  browserLocalAuthorRepository,
  releaseBrowserLocalAuthorRepository,
} from "./local-author-repository.js";

const REPOSITORY_VERSION = 1;
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.authorship-profile-repository.v1",
);
const BROWSER_REPOSITORY_PROPERTY =
  "__fishystuffAuthorshipProfileRepository";
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-authorship-profile-repository.v1",
);

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) {
    next.authoritative = freezeState(next.authoritative);
  }
  return Object.freeze(next);
}

function projectionState(value) {
  const projection = decodeAuthorshipProfileProjectionV1(value);
  return projection.kind === "missing"
    ? freezeState({ kind: "missing" })
    : freezeState({
      kind: "ready",
      revision: projection.revision,
      authorId: projection.authorId,
      displayName: projection.displayName,
      handle: projection.handle,
    });
}

function boundaryState(remoteState) {
  if (
    remoteState?.kind === "uninitialized"
    || remoteState?.kind === "loading"
  ) {
    return freezeState({ kind: remoteState.kind });
  }
  if (remoteState?.kind === "locked") {
    return freezeState({
      kind: "locked",
      reason: remoteState.reason || "account-locked",
    });
  }
  if (remoteState?.kind === "unavailable") {
    return freezeState({
      kind: "unavailable",
      operation: remoteState.operation || "account",
      reason: remoteState.reason || "account-state-unavailable",
    });
  }
  return freezeState({
    kind: "unavailable",
    operation: "account",
    reason: "account-authorship-profile-state-invalid",
  });
}

function pendingState(remoteState) {
  return freezeState({
    kind: "pending",
    operation: remoteState.operation,
    authoritative: projectionState(remoteState.authoritativeState),
  });
}

function conflictState(remoteState) {
  const authoritative = projectionState(remoteState.authoritativeState);
  return freezeState({
    kind: "conflict",
    operation: remoteState.operation,
    expectedRevision: remoteState.expectedRevision,
    actualRevision: authoritative.kind === "ready"
      ? authoritative.revision
      : null,
    authorId: authoritative.kind === "ready"
      ? authoritative.authorId
      : null,
    displayName: authoritative.kind === "ready"
      ? authoritative.displayName
      : "",
    handle: authoritative.kind === "ready"
      ? authoritative.handle
      : "",
    authoritative,
  });
}

function requireLocalRepository(repository) {
  if (
    !hasMethods(repository, [
      "ensureAuthor",
      "historicalAuthor",
      "load",
      "saveIdentity",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Authorship profiles require the exact local author repository contract.",
    );
  }
  return repository;
}

function importedRevisionOptions(
  options,
  stateBeforePreparation,
  preparation,
  repository,
) {
  if (
    stateBeforePreparation?.kind !== "missing"
    || options?.expectedRevision !== null
    || preparation?.kind !== "complete"
    || preparation.authorPhase !== "imported"
    || typeof preparation.authorRevision !== "string"
  ) {
    return options;
  }
  const current = repository.state();
  if (
    current?.kind !== "ready"
    || current.revision !== preparation.authorRevision
  ) {
    return options;
  }
  return { ...options, expectedRevision: preparation.authorRevision };
}

function preparationSettled(state) {
  return ["complete", "discarded", "missing"].includes(state?.kind);
}

export function createAccountAwareAuthorshipProfileRepository({
  localRepositoryFactory,
  releaseLocalRepository,
  authenticatedRepository,
  prepareLocalAuthor = initializeBrowserLocalAuthor,
} = {}) {
  if (
    typeof localRepositoryFactory !== "function"
    || typeof releaseLocalRepository !== "function"
    || typeof prepareLocalAuthor !== "function"
  ) {
    throw new TypeError(
      "Authorship profiles require their local lifecycle and preparation boundaries.",
    );
  }
  if (
    !hasMethods(authenticatedRepository, [
      "dispose",
      "ensureAuthor",
      "load",
      "saveIdentity",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Authorship profiles require the authenticated repository contract.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authority = "unknown";
  let accountAuthoritative = null;
  let localRepository = null;
  let unsubscribeLocal = null;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function historicalAuthor() {
    if (authority === "account") {
      return historicalAuthorshipProfileProjection(accountAuthoritative);
    }
    if (authority !== "local" || !localRepository) return null;
    const direct = localRepository.historicalAuthor();
    if (direct) return direct;
    if (
      currentState.kind === "conflict"
      && typeof currentState.authorId === "string"
      && currentState.authorId.length > 0
    ) {
      return historicalAuthorshipProfileProjection({
        kind: "ready",
        authorId: currentState.authorId,
        displayName: currentState.displayName,
        handle: currentState.handle,
      });
    }
    return null;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Authorship profile subscriber failed."),
      );
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return currentState;
    currentState = frozen;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function releaseLocal() {
    if (!localRepository) return;
    const repository = localRepository;
    authority = "unknown";
    localRepository = null;
    unsubscribeLocal?.();
    unsubscribeLocal = null;
    try {
      releaseLocalRepository(repository);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Local authorship profile release failed."),
      );
    }
  }

  function activateLocal() {
    if (disposed || (authority === "local" && localRepository)) return state();
    releaseLocal();
    accountAuthoritative = null;
    let repository = null;
    try {
      repository = requireLocalRepository(localRepositoryFactory());
      const initial = repository.state();
      const loaded = initial?.kind === "uninitialized"
        ? repository.load()
        : initial;
      authority = "local";
      localRepository = repository;
      unsubscribeLocal = repository.subscribe((nextState) => {
        if (
          !disposed
          && authority === "local"
          && repository === localRepository
        ) {
          publish(nextState);
        }
      });
      return publish(loaded ?? repository.state());
    } catch (_error) {
      if (repository) {
        try {
          releaseLocalRepository(repository);
        } catch (_releaseError) {
          // The unavailable projection below remains the public failure.
        }
      }
      authority = "unknown";
      localRepository = null;
      unsubscribeLocal = null;
      return publish({
        kind: "unavailable",
        operation: "load",
        reason: "local-authorship-profile-load-failed",
      });
    }
  }

  function projectAuthenticated(remoteState) {
    if (disposed) return;
    if (remoteState?.kind === "signedOut") {
      activateLocal();
      return;
    }

    releaseLocal();
    try {
      if (remoteState?.kind === "ready") {
        authority = "account";
        accountAuthoritative = projectionState(remoteState.authoritativeState);
        publish(accountAuthoritative);
        return;
      }
      if (remoteState?.kind === "pending") {
        authority = "account";
        accountAuthoritative = projectionState(remoteState.authoritativeState);
        publish(pendingState(remoteState));
        return;
      }
      if (remoteState?.kind === "conflict") {
        authority = "account";
        accountAuthoritative = projectionState(remoteState.authoritativeState);
        publish(conflictState(remoteState));
        return;
      }
      authority = "unknown";
      accountAuthoritative = null;
      publish(boundaryState(remoteState));
    } catch (_error) {
      authority = "unknown";
      accountAuthoritative = null;
      publish({
        kind: "unavailable",
        operation: "load",
        reason: "account-authorship-profile-state-invalid",
      });
    }
  }

  const unsubscribeAuthenticated = authenticatedRepository.subscribe(
    projectAuthenticated,
  );

  async function load() {
    if (disposed) return state();
    await authenticatedRepository.load();
    return state();
  }

  async function prepareLocalCommand(options) {
    const repository = localRepository;
    if (disposed || authority !== "local" || !repository) return null;
    const stateBeforePreparation = repository.state();
    let preparation;
    try {
      preparation = await prepareLocalAuthor();
    } catch (_error) {
      preparation = null;
    }
    if (
      disposed
      || authority !== "local"
      || repository !== localRepository
    ) {
      return null;
    }
    if (!preparationSettled(preparation)) {
      publish({
        kind: "unavailable",
        operation: "prepare",
        reason: "local-author-import-unavailable",
      });
      return null;
    }
    return Object.freeze({
      repository,
      options: importedRevisionOptions(
        options,
        stateBeforePreparation,
        preparation,
        repository,
      ),
    });
  }

  async function saveIdentity(identity, options) {
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "account") {
      await authenticatedRepository.saveIdentity(identity, options);
      return state();
    }
    const selected = await prepareLocalCommand(options);
    if (!selected) return state();
    await selected.repository.saveIdentity(identity, selected.options);
    return state();
  }

  async function ensureAuthor(options) {
    if (disposed || currentState.kind === "pending") return state();
    if (authority === "account") {
      await authenticatedRepository.ensureAuthor(options);
      return state();
    }
    const selected = await prepareLocalCommand(options);
    if (!selected) return state();
    await selected.repository.ensureAuthor(selected.options);
    return state();
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Authorship profile listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeAuthenticated();
    releaseLocal();
    accountAuthoritative = null;
    listeners.clear();
    authenticatedRepository.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: REPOSITORY_VERSION,
    dispose,
    ensureAuthor,
    historicalAuthor,
    load,
    saveIdentity,
    state,
    subscribe,
  });
}

let browserOwner = null;
let browserRepository = null;
let browserWindow = null;

function browserWindowRef() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("Browser authorship profiles require a window.");
  }
  return windowRef;
}

function disposeBrowserOwner() {
  browserOwner?.dispose();
  browserOwner = null;
  browserRepository = null;
  browserWindow = null;
}

function compatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_REPOSITORY_BRAND] === REPOSITORY_VERSION
    && hasMethods(value, [
      "ensureAuthor",
      "historicalAuthor",
      "load",
      "saveIdentity",
      "state",
      "subscribe",
    ])
    && value.dispose === undefined,
  );
}

function browserRepositoryView(owner) {
  return Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: REPOSITORY_VERSION,
    ensureAuthor: owner.ensureAuthor,
    historicalAuthor: owner.historicalAuthor,
    load: owner.load,
    saveIdentity: owner.saveIdentity,
    state: owner.state,
    subscribe: owner.subscribe,
  });
}

export function browserAuthorshipProfileRepository() {
  const windowRef = browserWindowRef();
  if (browserOwner && browserWindow !== windowRef) {
    disposeBrowserOwner();
  }

  const sharedRepository = windowRef[BROWSER_REPOSITORY_PROPERTY];
  if (sharedRepository !== undefined) {
    if (!compatibleBrowserRepository(sharedRepository)) {
      throw new TypeError(
        "The shared authorship profile repository has an incompatible contract.",
      );
    }
    if (browserRepository !== sharedRepository) {
      browserOwner?.dispose();
      browserOwner = null;
    }
    browserRepository = sharedRepository;
    browserWindow = windowRef;
    return browserRepository;
  }

  if (!browserOwner) {
    const authenticatedRepository =
      createAuthenticatedAuthorshipProfileRepository({
        sessionRepository: browserAccountStateSessionRepository(),
        apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
      });
    browserOwner = createAccountAwareAuthorshipProfileRepository({
      localRepositoryFactory: browserLocalAuthorRepository,
      releaseLocalRepository: releaseBrowserLocalAuthorRepository,
      authenticatedRepository,
    });
    browserRepository = browserRepositoryView(browserOwner);
    browserWindow = windowRef;
    Object.defineProperty(windowRef, BROWSER_REPOSITORY_PROPERTY, {
      configurable: true,
      value: browserRepository,
      writable: false,
    });
    void browserOwner.load();
  }
  return browserRepository;
}
